import { createContext, Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { useFindOneProduct } from 'modules/inventory/product/hooks/useFindOneProduct';
import { useParams } from 'react-router';
import { useBreadcrumbName } from '@dfl/mui-admin-layout';
import { IProduct } from 'modules/inventory/common/interfaces';
import { useSearchParams } from 'react-router-dom';
import useMultipleToggle from 'hooks/useMultipleToggle';

const states = {
  form_1: false,
  form_2: false,
  form_3: false,
  form_4: false,
  form_5: false,
  form_6: false,
  form_7: false,
  form_8: false,
  form_9: false,
  // section combo (pestana General). Clave con nombre, como las de SEO: de
  // form_1 a form_9 ya estan todas ocupadas.
  form_combo: false,
  form_extra_attributes: false,
  // section seo
  form_seo: false,
  form_description: false,
  form_internal_seo: false,
  form_seo_og: false,
  form_seo_twitter: false,
  form_all_seo: false,
};

// Data value of the provider context
type ProductContextValue = {
  product?: IProduct;
  setProduct?: Dispatch<SetStateAction<IProduct | undefined>>;
  seo?: Partial<Pick<IProduct, 'seo' | 'internalSeo' | 'slug'>>;
  setSeo?: Dispatch<SetStateAction<Partial<Pick<IProduct, 'seo' | 'internalSeo'>> | undefined>>;
  isLoading: boolean;
  error?: any;
  id: string;
  onAllToggle?: (open?: boolean) => void;
  onOneClose?: (st: string) => void;
  onOneOpen?: (st: string) => void;
  onOneToggle?: (st: string) => void;
  state?: Record<keyof typeof states, boolean>;
  allOpen?: boolean;
};
// default value of the context
const defaultValue: ProductContextValue = {
  isLoading: true,
  id: '',
};

// create context
const ProductContext = createContext<ProductContextValue>(defaultValue);

type ProductContextProps = {
  children: any;
};

/**
 * Provider component
 * */
const ProductDetailProvider = (props: ProductContextProps) => {
  const { id } = useParams();
  const productId: string = id as string;
  const [searchParams] = useSearchParams();
  const isEdit = searchParams.get('edit');
  const { onAllToggle, onOneClose, onOneOpen, onOneToggle, state, allOpen } = useMultipleToggle(states);
  const { isLoading, data, error } = useFindOneProduct(productId ?? null);
  const [product, setProduct] = useState<IProduct>();
  const [seo, setSeo] = useState<Partial<Pick<IProduct, 'seo' | 'internalSeo' | 'slug'>>>();

  useBreadcrumbName(id || '', product?.name, isLoading);

  useEffect(() => {
    if (isEdit && !isLoading && data && !product) {
      onAllToggle?.();
    }
  }, [isEdit, data, onAllToggle, isLoading, product]);

  useEffect(() => {
    if (data) {
      setProduct(data);
    }
  }, [data, setProduct]);

  useEffect(() => {
    if (data) {
      setSeo({ seo: data?.seo, internalSeo: data?.internalSeo, slug: data?.slug });
    }
  }, [data, setSeo]);

  return (
    <ProductContext.Provider
      value={{
        id: productId,
        product,
        setProduct,
        isLoading,
        error,
        onAllToggle,
        onOneClose,
        onOneOpen,
        onOneToggle,
        state,
        allOpen,
        seo,
        setSeo,
      }}
      {...props}
    />
  );
};

// Default hooks to retrieve context data
const useProductDetail = () => {
  const context = useContext(ProductContext);
  if (context === undefined) {
    throw new Error('You must be inside a UserDetailProvider component');
  }
  return context;
};

/**
 * Lightweight value-provider: feeds an explicit product value into the same
 * ProductContext without fetching by route param. Used to reuse the product
 * detail price/stock forms for a VARIANT (a different product than the page's),
 * so those forms read the variant as their "current product" for display bits.
 */
const ProductDetailValueProvider = ({
  value,
  children,
}: {
  value: ProductContextValue;
  children: any;
}) => <ProductContext.Provider value={value}>{children}</ProductContext.Provider>;

export { ProductContext, ProductDetailProvider, ProductDetailValueProvider, useProductDetail };
export type { ProductContextValue };
